(() => {
  const existingBootstrap = window.ChefollaRatingOverview;

  if (existingBootstrap) {
    existingBootstrap.initAll(document);
    return;
  }

  const DEFAULT_ROTATION_INTERVAL = 4000;
  const MIN_ROTATION_INTERVAL_SECONDS = 1;
  const MAX_ROTATION_INTERVAL_SECONDS = 10;
  const TRANSITION_DURATION = 450;

  function getRotationInterval(value) {
    const seconds = Number(value);
    if (
      !Number.isFinite(seconds) ||
      seconds < MIN_ROTATION_INTERVAL_SECONDS ||
      seconds > MAX_ROTATION_INTERVAL_SECONDS
    ) {
      return DEFAULT_ROTATION_INTERVAL;
    }

    return seconds * 1000;
  }

  class RatingOverview {
    constructor(root) {
      this.root = root;
      this.viewport = root.querySelector('[data-review-viewport]');
      this.panels = this.viewport ? Array.from(this.viewport.querySelectorAll('[data-review-panel]')) : [];
      this.rotationInterval = getRotationInterval(root.dataset.rotationInterval);
      this.activeIndex = 0;
      this.pauseReasons = new Set();
      this.timer = null;
      this.transitionTimer = null;
      this.resizeFrame = null;
      this.isTransitioning = false;

      this.handleMouseEnter = () => this.pause('hover');
      this.handleMouseLeave = () => this.resume('hover');
      this.handleFocusIn = () => this.pause('focus');
      this.handleFocusOut = () => {
        window.setTimeout(() => {
          if (!this.root.contains(document.activeElement)) this.resume('focus');
        }, 0);
      };
      this.handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          this.resume('visibility');
        } else {
          this.pause('visibility');
        }
      };
      this.handleResize = () => {
        window.cancelAnimationFrame(this.resizeFrame);
        this.resizeFrame = window.requestAnimationFrame(() => this.measure());
      };

      this.init();
    }

    init() {
      if (!this.viewport || this.panels.length === 0) return;

      this.panels.forEach((panel, index) => {
        panel.classList.toggle('is-active', index === 0);
        panel.classList.remove('is-entering', 'is-exiting');
        panel.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
      });

      this.measure();
      document.fonts?.ready.then(() => this.measure());
      window.addEventListener('resize', this.handleResize, { passive: true });

      if (this.panels.length < 2) return;

      this.root.addEventListener('mouseenter', this.handleMouseEnter);
      this.root.addEventListener('mouseleave', this.handleMouseLeave);
      this.root.addEventListener('focusin', this.handleFocusIn);
      this.root.addEventListener('focusout', this.handleFocusOut);
      document.addEventListener('visibilitychange', this.handleVisibilityChange);

      if (document.visibilityState !== 'visible') this.pauseReasons.add('visibility');
      this.start();
    }

    measure(panel = null) {
      if (!this.viewport || this.panels.length === 0) return;

      if (window.matchMedia('(max-width: 749px)').matches) {
        const activePanel = panel || this.panels[this.activeIndex];
        const activePanelHeight = activePanel?.scrollHeight || 0;
        if (activePanelHeight > 0) this.viewport.style.height = `${activePanelHeight}px`;
        return;
      }

      const tallestPanel = this.panels.reduce((height, panel) => Math.max(height, panel.scrollHeight), 0);
      if (tallestPanel > 0) this.viewport.style.height = `${tallestPanel}px`;
    }

    start() {
      this.stop();
      if (this.panels.length < 2 || this.pauseReasons.size > 0) return;
      this.timer = window.setInterval(() => this.showNext(), this.rotationInterval);
    }

    stop() {
      if (!this.timer) return;
      window.clearInterval(this.timer);
      this.timer = null;
    }

    pause(reason) {
      this.pauseReasons.add(reason);
      this.stop();
    }

    resume(reason) {
      this.pauseReasons.delete(reason);
      if (this.pauseReasons.size === 0) this.start();
    }

    showNext() {
      if (this.isTransitioning || this.panels.length < 2) return;

      const currentPanel = this.panels[this.activeIndex];
      const nextIndex = (this.activeIndex + 1) % this.panels.length;
      const nextPanel = this.panels[nextIndex];

      this.isTransitioning = true;
      nextPanel.classList.add('is-entering');
      nextPanel.setAttribute('aria-hidden', 'false');
      this.measure(nextPanel);

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          currentPanel.classList.remove('is-active');
          currentPanel.classList.add('is-exiting');
          currentPanel.setAttribute('aria-hidden', 'true');
          nextPanel.classList.remove('is-entering');
          nextPanel.classList.add('is-active');
          this.activeIndex = nextIndex;

          window.clearTimeout(this.transitionTimer);
          this.transitionTimer = window.setTimeout(() => {
            currentPanel.classList.remove('is-exiting');
            this.isTransitioning = false;
          }, TRANSITION_DURATION);
        });
      });
    }

    destroy() {
      this.stop();
      window.clearTimeout(this.transitionTimer);
      window.cancelAnimationFrame(this.resizeFrame);
      window.removeEventListener('resize', this.handleResize);
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      this.root.removeEventListener('mouseenter', this.handleMouseEnter);
      this.root.removeEventListener('mouseleave', this.handleMouseLeave);
      this.root.removeEventListener('focusin', this.handleFocusIn);
      this.root.removeEventListener('focusout', this.handleFocusOut);
      delete this.root.ratingOverviewInstance;
    }
  }

  function initAll(container) {
    const roots = [];
    if (container.matches?.('[data-rating-overview]')) roots.push(container);
    roots.push(...container.querySelectorAll?.('[data-rating-overview]') || []);

    roots.forEach((root) => {
      if (!root.ratingOverviewInstance) root.ratingOverviewInstance = new RatingOverview(root);
    });
  }

  function destroyAll(container) {
    const roots = [];
    if (container.matches?.('[data-rating-overview]')) roots.push(container);
    roots.push(...container.querySelectorAll?.('[data-rating-overview]') || []);
    roots.forEach((root) => root.ratingOverviewInstance?.destroy());
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyAll(event.target));

  window.ChefollaRatingOverview = { initAll, destroyAll };
  initAll(document);
})();
